import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, Select, MenuItem, FormControl,
  InputLabel, TextField, IconButton, Collapse
} from '@mui/material';
import { HelpOutline, Save, ExpandMore, ExpandLess } from '@mui/icons-material';
import { toast } from 'react-toastify';

const statusColors = { new: 'warning', reviewed: 'info', sourced: 'success', closed: 'default' };

const AdminProductRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const [saving, setSaving] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/product-requests');
      setRequests(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/product-requests/${id}`, { status });
      toast.success('Status updated');
      load();
    } catch (err) { toast.error('Failed to update'); }
  };

  const saveNotes = async (id) => {
    setSaving(id);
    try {
      await api.put(`/product-requests/${id}`, { adminNotes: notesDraft[id] });
      toast.success('Notes saved');
      load();
    } catch (err) { toast.error('Failed to save notes'); }
    setSaving('');
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        <HelpOutline sx={{ verticalAlign: 'middle', mr: 1 }} />
        Product Requests
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Products customers asked Sam for that aren't in the catalog yet, filed automatically during AI conversations.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell />
              <TableCell>Product Wanted</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Filed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((r) => (
              <React.Fragment key={r._id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => setExpandedId(expandedId === r._id ? null : r._id)}>
                      {expandedId === r._id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{r.productName}</TableCell>
                  <TableCell>
                    {r.customer?.firstName} {r.customer?.lastName}
                    <Typography variant="caption" display="block" color="text.secondary">{r.customer?.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select value={r.status} onChange={(e) => updateStatus(r._id, e.target.value)}>
                        {['new', 'reviewed', 'sourced', 'closed'].map((s) => (
                          <MenuItem key={s} value={s}>
                            <Chip size="small" label={s} color={statusColors[s]} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                    <Collapse in={expandedId === r._id} unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Customer's details</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{r.details}</Typography>
                        <TextField
                          fullWidth multiline minRows={2} size="small" label="Internal notes"
                          value={notesDraft[r._id] ?? r.adminNotes ?? ''}
                          onChange={(e) => setNotesDraft((prev) => ({ ...prev, [r._id]: e.target.value }))}
                          sx={{ mb: 1 }}
                        />
                        <IconButton size="small" color="primary" disabled={saving === r._id} onClick={() => saveNotes(r._id)}>
                          <Save fontSize="small" />
                        </IconButton>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {requests.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center">No product requests filed yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminProductRequests;
